import { z } from "zod";
import { ColumnMap, StatementFile, StatementFormat } from "../domain.js";
import { ErrorCode } from "../errors.js";
import { Id, IsoDate, IsoMonth, Pence } from "../primitives.js";
import { StatementProfile } from "../statements/profile.js";

/**
 * Upload archives the file and returns a **preview**. Committing is a second
 * call. Nothing enters the ledger without being seen first — which is the same
 * promise the rule engine makes, for the same reason.
 */

export const StatementPreviewRow = z.object({
  line: z.number().int(),
  date: IsoDate,
  description: z.string(),
  amountPence: Pence,
  /** Already in the ledger under this hash, so committing will skip it. */
  duplicate: z.boolean(),
  categoryId: Id.nullable(),
  category: z.string(),
});

export const StatementSkippedRow = z.object({
  line: z.number().int(),
  reason: z.string(),
});

/**
 * What re-adding the statement's own printed balances proved.
 *
 * `broken` is not here on purpose: an import that does not add up is refused
 * with a 422 rather than previewed, so the only states a client can see are
 * "checked, and it agrees" and "there was nothing to check".
 */
export const StatementReconciliation = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("verified"),
    /** How many printed balances were re-added and agreed. */
    checkpoints: z.number().int(),
    openingPence: Pence,
    closingPence: Pence,
  }),
  z.object({
    status: z.literal("unchecked"),
    reason: z.enum(["no-balance-column", "no-opening-balance", "no-checkpoints"]),
  }),
]);

/**
 * One ledger found inside an uploaded file.
 *
 * A statement file is not always a statement: banks bundle a current account
 * with the savings account beside it, a card with its second cardholder, a
 * whole household's accounts in one pack. The tables share a header, so they
 * are told apart by their running balances rather than by any phrase.
 *
 * `sample` is what makes the picker usable — "Transfer to Pot" against
 * "Deposit (round up)" is how a person recognises which account is which, and
 * no amount of dates and counts substitutes for it.
 */
export const StatementLedger = z.object({
  index: z.number().int(),
  rowCount: z.number().int(),
  firstDate: IsoDate,
  lastDate: IsoDate,
  /** The last printed balance, which is this ledger's closing figure. */
  closingPence: Pence.nullable(),
  pages: z.array(z.number().int()),
  sample: z.array(z.string()),
});

export const StatementPreview = z.object({
  /** The preview token: the archived file's id. Committing quotes it back. */
  statementId: Id,
  filename: z.string(),
  accountId: Id,
  accountLabel: z.string(),
  parsed: z.number().int(),
  toImport: z.number().int(),
  duplicates: z.number().int(),
  uncategorised: z.number().int(),
  months: z.array(IsoMonth),
  /** Signed sum of what would be added — the figure the month moves by. */
  netPence: Pence,
  rows: z.array(StatementPreviewRow),
  /** True when `rows` is a sample. The counts above are always the whole file. */
  truncated: z.boolean(),
  skipped: z.array(StatementSkippedRow),
  /** Rules whose pattern would not compile, surfaced rather than swallowed. */
  invalidRules: z.array(z.object({ ruleId: Id, pattern: z.string(), reason: z.string() })),
  /** Null for a CSV, which prints no running balance to check against. */
  reconciliation: StatementReconciliation.nullable(),
  /**
   * The other ledgers in the same file, which this import leaves alone.
   *
   * Never empty silently: importing 29 of a file's 42 rows is something the
   * person is told, not something they discover. A wrong layout can split one
   * real statement in two, and that failure looks identical to a correct split
   * from the inside — so the count is put in front of whoever presses Import.
   */
  otherLedgers: z.array(StatementLedger),
});

/**
 * Uploading a PDF has two outcomes, and "we do not recognise this layout yet"
 * is one of them rather than an error.
 *
 * It travels in the response body rather than through `HttpError` because it
 * carries a payload the client acts on, and every response in this API is
 * validated by a shared schema at both ends. An error shape would put it
 * outside that.
 */
export const StatementUploadResult = z.discriminatedUnion("status", [
  StatementPreview.extend({ status: z.literal("preview") }),
  z.object({
    status: z.literal("layout_unknown"),
    /** Names the sample stashed in R2. There is no second upload. */
    token: z.string(),
    fingerprint: z.string(),
    filename: z.string(),
    pages: z.number().int(),
  }),
  /**
   * The file holds several ledgers and the statement does not say which one it
   * is about — no labelled closing balance, or one that two of them share.
   *
   * An outcome, not an error, and a question rather than a guess: picking the
   * first would silently import a different account's transactions, which is
   * the one mistake here that is expensive to notice and worse to undo. The
   * file is stashed exactly as an unknown layout is, so answering costs no
   * second upload.
   */
  z.object({
    status: z.literal("ledger_choice"),
    token: z.string(),
    filename: z.string(),
    ledgers: z.array(StatementLedger),
  }),
]);

/** Answering the question above. The token names the stashed file. */
export const StatementLedgerChoice = z.object({
  token: Id,
  accountId: Id,
  ledgerIndex: z.number().int().min(0).max(100),
});

export const StatementCommitResult = z.object({
  statementId: Id,
  imported: z.number().int(),
  duplicates: z.number().int(),
  uncategorised: z.number().int(),
  months: z.array(IsoMonth),
  netPence: Pence,
});

export const StatementList = z.object({ data: z.array(StatementFile) });

/**
 * Correcting an import filed under the wrong account. Only an already-imported
 * statement can be reassigned — its rows move with it, re-keyed under the new
 * account so future duplicate detection still works.
 */
export const StatementReassign = z.object({ accountId: Id });

/** Which account the file belongs to. Absent means "work it out from the name". */
export const StatementUploadFields = z.object({
  accountId: Id.optional(),
});

/**
 * The mapping tool's look at a file before anything is saved. No account, no
 * R2, no DB — just the raw rows to show, a guess at the header, which columns
 * are already understood, and (once the caller has a candidate mapping) a
 * sample of what it would actually produce, via the same pipeline a real
 * import runs.
 */
export const StatementSniffPreviewRow = z.object({
  line: z.number().int(),
  date: IsoDate,
  description: z.string(),
  amountPence: Pence,
});

export const StatementSniffResult = z.object({
  rows: z.array(z.array(z.string())),
  rowsTruncated: z.boolean(),
  guessedHeaderRow: z.number().int(),
  recognisedRoles: ColumnMap,
  /** Full count, not the sample size below. */
  parsed: z.number().int(),
  preview: z.array(StatementSniffPreviewRow),
  skippedCount: z.number().int(),
  skipped: z.array(StatementSkippedRow),
});

/** Multipart fields alongside the file. `columnMap` travels as a JSON string. */
export const StatementSniffFields = z.object({
  format: StatementFormat.optional(),
  columnMap: z.string().optional(),
});

export type StatementPreviewRow = z.infer<typeof StatementPreviewRow>;
export type StatementSkippedRow = z.infer<typeof StatementSkippedRow>;
export type StatementLedger = z.infer<typeof StatementLedger>;
export type StatementLedgerChoice = z.infer<typeof StatementLedgerChoice>;
export type StatementPreview = z.infer<typeof StatementPreview>;
export type StatementReconciliation = z.infer<typeof StatementReconciliation>;
export type StatementUploadResult = z.infer<typeof StatementUploadResult>;
export type StatementCommitResult = z.infer<typeof StatementCommitResult>;
export type StatementList = z.infer<typeof StatementList>;
export type StatementReassign = z.infer<typeof StatementReassign>;
export type StatementUploadFields = z.infer<typeof StatementUploadFields>;
export type StatementSniffPreviewRow = z.infer<typeof StatementSniffPreviewRow>;
export type StatementSniffResult = z.infer<typeof StatementSniffResult>;
export type StatementSniffFields = z.infer<typeof StatementSniffFields>;

/**
 * Setting up a layout nobody has described yet.
 *
 * The draft carries the whole profile, not a summary of it: the client hands it
 * back on approval, and the server re-runs it against the file rather than
 * trusting it. What makes approval safe is the re-run, not the round trip.
 */
export const StatementProfileRow = z.object({
  date: IsoDate,
  description: z.string(),
  amountPence: Pence,
});

export const StatementProfileDraft = z.object({
  token: z.string(),
  fingerprint: z.string(),
  profile: StatementProfile,
  /** What the model was unsure about, for the person reviewing it. */
  notes: z.string(),
  rowCount: z.number().int(),
  /**
   * The period the whole statement covers.
   *
   * Carried rather than derived from `rows`, which is a sample: a newest-first
   * statement's first ten rows are its last few days, so reading the range off
   * them told someone approving a full month's import that it covered five
   * days. The count and the range have to describe the same thing.
   */
  firstDate: IsoDate,
  lastDate: IsoDate,
  /** A sample for the review step, not the import. `rowCount` is the whole of it. */
  rows: z.array(StatementProfileRow),
  reconciliation: StatementReconciliation,
  /** Words inside the table that no column claimed. A high count means drift. */
  strayWords: z.number().int(),
  /**
   * How many ledgers the file turned out to hold.
   *
   * Shown before approval because the rows above are one of them. A layout that
   * splits a single statement into several is wrong in a way that looks correct
   * from the inside, and this is the number that gives it away.
   */
  ledgerCount: z.number().int(),
});

/**
 * `Id` rather than a free string, in both of these: the token names an object in
 * R2, and an id cannot contain a slash or a dot — so it cannot climb out of the
 * household's own prefix.
 */
export const StatementProfileDerive = z.object({ token: Id });

export const StatementProfileSave = z.object({
  token: Id,
  accountId: Id,
  label: z.string().trim().min(1).max(80),
  /**
   * The layout to save. It may have been edited since it was drafted, and it
   * may never have been drafted at all — a profile written by hand arrives the
   * same way. Either is fine: it is re-run against the statement before it is
   * accepted.
   */
  profile: StatementProfile,
});

/**
 * Deriving a layout is a **stream**, for the same reason asking a question is.
 *
 * A high-effort read of a page of geometry is a minute-scale call, and a
 * minute-scale call with no bytes on the wire is at the mercy of everything
 * between the Worker and the browser: the edge's proxy read timeout, a phone
 * that backgrounds the tab, a captive portal that trims idle connections. None
 * of those exist in front of `wrangler dev`, which is exactly why this failed
 * only in production.
 *
 * So the frames start arriving immediately and keep arriving. `working` carries
 * nothing and means nothing except that the model is still going — it is bytes,
 * and bytes are the point. A client that does not care about progress can
 * ignore every frame but the last two.
 */
export const StatementProfileEvent = z.discriminatedUnion("type", [
  /** Sent before the model is called, so the client knows the stream is live. */
  z.object({ type: z.literal("started"), fingerprint: z.string(), pages: z.number().int() }),
  z.object({ type: z.literal("working") }),
  /**
   * The first answer did not survive the reconciler and is being asked again.
   * Worth showing: it is the difference between "slow" and "stuck".
   */
  z.object({ type: z.literal("retrying"), reason: z.string() }),
  z.object({ type: z.literal("draft"), draft: StatementProfileDraft }),
  /**
   * A failure after the stream opened. The status is already 200 by then, so
   * the error travels in the stream — the same arrangement `AskEvent` uses,
   * and for the same reason.
   */
  z.object({ type: z.literal("error"), code: ErrorCode, message: z.string() }),
]);

export const StatementProfileEntry = z.object({
  id: Id,
  label: z.string(),
  fingerprint: z.string(),
  source: z.enum(["derived", "manual"]),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
  /** False when the stored layout no longer satisfies the schema — retire it. */
  valid: z.boolean(),
});

export const StatementProfileList = z.object({ data: z.array(StatementProfileEntry) });

export type StatementProfileRow = z.infer<typeof StatementProfileRow>;
export type StatementProfileDerive = z.infer<typeof StatementProfileDerive>;
export type StatementProfileDraft = z.infer<typeof StatementProfileDraft>;
export type StatementProfileSave = z.infer<typeof StatementProfileSave>;
export type StatementProfileEvent = z.infer<typeof StatementProfileEvent>;
export type StatementProfileEntry = z.infer<typeof StatementProfileEntry>;
export type StatementProfileList = z.infer<typeof StatementProfileList>;
